import { useCallback } from 'react';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import type { Market } from '@//config/domain-locale-map';
import { useLegacyTracking } from '@//providers/LegacyTrackingProvider';
import { useMarket } from '@//providers/MarketSettingsProvider';
import type { LegacyTrackFinanceInterestedEventData } from './legacy-types';
import { useLegacyTrackShownInterest } from './useLegacyTrackShownInterest';
import { mapElasticVehicleToLegacyItem } from './utils';

interface UseLegacyTrackFinanceInterestedVariables {
  vehicle: ElasticVehicle;
  information: LegacyTrackFinanceInterestedEventData['information'];
}

export const createLegacyTrackFinanceInterested = (
  market: Market,
  variables: UseLegacyTrackFinanceInterestedVariables,
): LegacyTrackFinanceInterestedEventData => {
  const eventData: LegacyTrackFinanceInterestedEventData = {
    eventType: 'microConversion',
    conversionType: 'financeInterest',
    item: mapElasticVehicleToLegacyItem(market, variables.vehicle),
    information: variables.information,
    pageType: 'product page',
  };

  return eventData;
};

export const useLegacyTrackFinanceInterested = () => {
  const market = useMarket();
  const { track } = useLegacyTracking();
  const { trackLegacyShownInterest } = useLegacyTrackShownInterest();

  const trackLegacyFinanceInterested = useCallback(
    (variables: UseLegacyTrackFinanceInterestedVariables) => {
      trackLegacyShownInterest(variables.vehicle);
      track({
        event: 'microConversion',
        data: createLegacyTrackFinanceInterested(market, variables),
      });
    },
    [market, track],
  );

  return {
    trackLegacyFinanceInterested,
  };
};
